import { System, SystemImpl } from "minecs";
import type { GameModel } from "yage/game/GameModel";
import { Transform } from "yage/schemas/entity/Transform";
import { AirportState, AirportPassenger, AirportStation, AirportPath, AirportPlane, AirportStaff, AirportTrash, AirportAgent } from "../components/index.js";
import { currentMission } from "../domain/missions.js";
import { CONFIG, SECURITY_WALK, onPad, VENDING, ENTRANCE, ENTRANCE_DOOR, STREET_ENTRY, PASSENGER_WALK_SPEED, vendingPoint, stageServing, nextStage, CORE_STAGES, PASSPORT, internationalTraveller, BINS, binPad, QUEUE_PATIENCE, SAD_RADIUS } from "../domain/config.js";
import { PHASE } from '../domain/operations.js';
import { arrivalFrames, capacity, serviceFrames, deposit, vendingPayout } from "../domain/economy.js";
import { state, career, ids, stations, make, route, walkStraight, distance, vending, litter, notice, resetPatience } from "../services/world.js";
import { gateWalk, arrivalStep, waitingSeats } from "../domain/gateflow.js";
import { tier } from "../domain/airports.js";
import { shiftsActive, RETAIL_MODULES } from "../domain/act2.js";
import { travellerRoute } from "../services/act2.js";
import { crisisMood, rushLive, celebrityLive } from "../services/events.js";
import { RUSH_RATE } from "../domain/events.js";
import { archetype, PET_CALM, BUSINESS_HANDLING, BUSINESS_PATIENCE, CONNECT_SPAN, CONNECT_DEADLINE, connectsHere, isConnector } from "../domain/passengers.js";
import { staffTrait } from "../domain/staff.js";
// Passenger states: 0 queue/walking, 1 walking to service, 2 served, 3 boarding, 4 vending, 5 walking through Security, 6 waiting in the gate lounge, 7 asking at the boarding desk.
import { queuePoint, servicePoint, inquiryPoint, boardingWorkPoint } from '../domain/queues.js';
import { worksDesk } from '../services/crew.js';
import { boardingGate } from '../services/operations.js';
import { automaticBoarding } from '../services/boarding.js';
export { queuePoint, servicePoint } from '../domain/queues.js';

/** Join at the tail and follow each bend, rather than cutting through the waiting crowd. */
function queueRoute(g: GameModel, id: number, index: number, rank: number, tail: number, gate = 0) {
    const path = g.getTypedUnsafe(AirportPath, id), at = g.getTypedUnsafe(Transform, id), target = queuePoint(index, rank, gate, tier(state(g)).code);
    if (Math.abs(path.targetX - target.x) < 1 && Math.abs(path.targetY - target.y) < 1) return;
    if (index === 6) {
        route(g, id, target, true);
        return;
    }
    let joined = false, entry = tail, nearest = Infinity;
    for (let n = 0; n <= 15; n++) {
        const point = queuePoint(index, n);
        if (Math.abs(path.targetX - point.x) < 1 && Math.abs(path.targetY - point.y) < 1) joined = true;
        const d = distance(at, point);
        if (d < nearest) { nearest = d; entry = n; }
    }
    joined = joined && nearest < 28;
    entry = joined ? Math.max(rank, entry) : tail;
    if (joined) {
        route(g, id, queuePoint(index, entry), true);
    } else {
        const s = CONFIG.stages[index], lanes = Math.max(Math.min(4, 2 + stations(g)[index].dividerLevel), Math.floor(tail / 3) + 1);
        const front = queuePoint(index, 0).y, back = queuePoint(index, 2).y;
        const left = s.x + 26 - Math.min(6, lanes) * 52 - 26;
        if (!route(g, id, { x: left, y: back + 48 }, true)) return;
        const enterY = Math.floor(tail / 3) % 2 ? front - 48 : back + 48;
        const point = queuePoint(index, entry);
        path.points.push(left, enterY, point.x, enterY, point.x, point.y);
    }
    for (let n = entry - 1; n >= rank; n--) { const point = queuePoint(index, n); path.points.push(point.x, point.y); }
    path.targetX = target.x; path.targetY = target.y;
}
/** A landed aircraft unloads at the apron stairs, then passengers cross the gate door to landside.
 *  Loads vary flight to flight — usually full, sometimes two-thirds — never an empty plane. */
export function landArrivals(g: GameModel, plane: AirportPlane) {
    const e = state(g), walk = gateWalk(tier(e).code, plane.gate, true);
    const headroom = Math.max(0, CONFIG.maxPassengers - ids(g, AirportPassenger).length);
    const count = Math.min(plane.seats - g.rand.int(0, Math.floor(plane.seats / 3)), headroom);
    for (let i = 0; i < count; i++) {
        make(g, 'Passenger', { Transform: walk[walk.length - 1], AirportPassenger: {
            serial: ++e.serial, arriving: true, stage: -1, gate: plane.gate, walkStep: arrivalStep(walk),
            releaseFrame: g.frame + i * 48, remaining: 6000 + i * 48, tint: g.rand.int(0, 5),
        }, AirportPath: { speed: PASSENGER_WALK_SPEED } });
    }
}
@System(AirportState)
export class AirportArrivals extends SystemImpl<GameModel> {
    static depth = 1100;
    run = (g: GameModel) => {
        const e = state(g);
        if (!e.loaded)
            return;
        // One point of dissatisfaction cools off every 30 simulated seconds, wherever arrivals stand.
        if (e.dissatisfied > 0 && g.frame % 1800 === 0)
            e.dissatisfied--;
        const planeIds = ids(g, AirportPlane);
        let allowUpcoming = true;
        if (planeIds.length === 1) {
            const plane = g.getTypedUnsafe(AirportPlane, planeIds[0]);
            const current = ids(g, AirportPassenger).map(id => g.getTypedUnsafe(AirportPassenger, id))
                .filter(p => !p.arriving).sort((a, b) => a.serial - b.serial)
                .slice(0, Math.max(0, plane.seats - plane.boarded));
            // The oldest load belongs to this turnaround; walk-through passengers are still in Security.
            const cleared = plane.boarded + current.filter(p => p.stage > 2 && p.state !== 5).length;
            allowUpcoming = cleared >= Math.ceil(plane.seats * 0.75);
        }
        // Multiple gates keep their independent current and upcoming passenger supply.
        const seats = planeIds.reduce((n, id) => {
            const p = g.getTypedUnsafe(AirportPlane, id);
            const current = p.phase === PHASE.BOARDING || p.phase === PHASE.READY ? Math.max(0, p.seats - p.boarded) : p.seats;
            const upcoming = allowUpcoming && (p.phase === PHASE.BOARDING || p.phase === PHASE.READY) ? p.seats : 0;
            return n + current + upcoming;
        }, 0);
        const enter = (y: number) => {
            const serial = ++e.serial;
            make(g, 'Passenger', {
                Transform: { x: STREET_ENTRY.x, y },
                AirportPassenger: { stage: -1, entering: true, serial, tint: g.rand.int(0, 5), vip: serial % 9 === 0 || shiftsActive(e) && celebrityLive(e) && serial % 3 === 0 },
                AirportPath: { speed: PASSENGER_WALK_SPEED, moving: true, targetX: STREET_ENTRY.x, targetY: ENTRANCE.y,
                    points: y > ENTRANCE_DOOR.y ? [STREET_ENTRY.x, ENTRANCE_DOOR.y, STREET_ENTRY.x, ENTRANCE.y] : [STREET_ENTRY.x, ENTRANCE.y] },
            });
        };
        if (e.serial === 0 && seats > 0) {
            // When the captain runs in, there should already be a line of passengers that were waiting at Check-In.
            const portSeats = tier(e).seats;
            const lineCount = Math.min(portSeats, 6);
            for (let rank = 0; rank < lineCount; rank++) {
                const point = queuePoint(0, rank);
                const serial = ++e.serial;
                make(g, 'Passenger', {
                    Transform: { x: point.x, y: point.y },
                    AirportPassenger: { stage: 0, state: 0, serial, tint: g.rand.int(0, 5), vip: serial % 9 === 0 },
                    AirportPath: { speed: PASSENGER_WALK_SPEED, moving: false, targetX: point.x, targetY: point.y, points: [] },
                });
            }
            e.spawnClock = 0;
            e.nextArrivalIn = Math.round(arrivalFrames(e.arrivalLevel) * (0.8 + g.rand.int(0, 60) / 100));
            return;
        }
        e.spawnClock++;
        if (e.spawnClock < (e.nextArrivalIn || arrivalFrames(e.arrivalLevel)) || seats === 0)
            return;
        // Passenger supply follows flight seats; unhappy travellers still keep their tickets.
        const room = seats;
        const travelers = ids(g, AirportPassenger).filter(id => !g.getTypedUnsafe(AirportPassenger, id).arriving).length;
        if (travelers >= room || ids(g, AirportPassenger).length >= CONFIG.maxPassengers)
            return;
        const first = ids(g, AirportPassenger).filter(id => g.getTypedUnsafe(AirportPassenger, id).stage === 0).length;
        if (first >= 12)
            return;
        e.spawnClock = 0;
        // A load trickles in, not pumps: every arrival resets a fresh, varied interval.
        // A forecast rush (MGT-019) brings travellers 1.75x as often while it lasts.
        e.nextArrivalIn = Math.round(arrivalFrames(e.arrivalLevel) * (0.8 + g.rand.int(0, 60) / 100) / (shiftsActive(e) && rushLive(e) ? RUSH_RATE : 1));
        enter(STREET_ENTRY.y);
    };
}
@System(AirportState)
export class AirportPassengerRouting extends SystemImpl<GameModel> {
    static depth = 1400;
    run = (g: GameModel) => {
        if (!state(g).loaded)
            return;
        const ss = stations(g);
        ss.forEach(s => { s.assignedWorkers = ids(g, AirportStaff).filter(id => { const worker = g.getTypedUnsafe(AirportStaff,id); return worksDesk(worker) && worker.stage === s.index; }).length; });
        for (const id of ids(g, AirportPassenger)) {
            const p = g.getTypedUnsafe(AirportPassenger, id);
            if (!p.entering) continue;
            const path = g.getTypedUnsafe(AirportPath, id), at = g.getTypedUnsafe(Transform, id);
            if (path.moving || distance(at, { x: STREET_ENTRY.x, y: ENTRANCE.y }) >= 8) continue;
            p.entering = false; p.stage = 0;
        }
        // Landed passengers walk out through the landside entrance; `remaining` doubles as a
        // despawn deadline so a blocked path can never strand one on the concourse.
        for (const id of ids(g, AirportPassenger)) {
            const p = g.getTypedUnsafe(AirportPassenger, id);
            if (!p.arriving) continue;
            if (g.frame < p.releaseFrame) continue;
            const walk = gateWalk(tier(state(g)).code, p.gate, true);
            const exit = { x: ENTRANCE.x + 24, y: ENTRANCE.y };
            const street = { x: ENTRANCE.x + 24, y: STREET_ENTRY.y };
            // Back down the gate walk (walkStep 1 is the last leg, doorway to inside), then the exit and the street.
            const target = p.walkStep < 2 ? walk[1 - p.walkStep] : p.walkStep === 2 ? exit : street;
            if (p.walkStep < 1) walkStraight(g, id, target); else if (p.walkStep < 3) route(g, id, target);
            const at = g.getTypedUnsafe(Transform, id);
            if (p.walkStep < 2 && distance(at, target) < 6) p.walkStep++;
            // A connecting traveller stays airside: from the gate doorway they join the departure lounge with a deadline.
            if (p.walkStep === 2 && p.serial < CONNECT_SPAN && connectsHere(p.serial, tier(state(g)).code, shiftsActive(state(g)))) {
                Object.assign(p, { arriving: false, serial: p.serial + CONNECT_SPAN, stage: 6, state: 0, gate: -1, walkStep: 0, queuedAt: g.frame, remaining: CONNECT_DEADLINE });
                resetPatience(p);
                continue;
            }
            if (p.walkStep === 2 && distance(at, exit) < 8) {
                p.walkStep = 3;
                Object.assign(g.getTypedUnsafe(AirportPath, id), { moving: true, cursor: 0, targetX: street.x, targetY: street.y,
                    points: [street.x, ENTRANCE_DOOR.y, street.x, street.y] });
            }
            if (p.walkStep === 3 && distance(at, street) < 8 || --p.remaining <= 0) g.removeEntity(id);
        }
        for (const id of ids(g, AirportPassenger)) {
            const p = g.getTypedUnsafe(AirportPassenger, id);
            if (p.state !== 5) continue;
            const target = SECURITY_WALK[p.slot];
            route(g, id, target);
            if (distance(g.getTypedUnsafe(Transform, id), target) < 8 && ++p.slot >= SECURITY_WALK.length) {
                p.state = p.stage === 6 ? 6 : 0; p.slot = -1;
            }
        }
        const trashPts = ids(g, AirportTrash).map(tid => g.getTypedUnsafe(Transform, tid));
        const nearTrash = (at: { x: number; y: number }) => trashPts.some(t => {
            const dx = t.x - at.x, dy = t.y - at.y;
            return dx * dx + dy * dy < SAD_RADIUS * SAD_RADIUS;
        });
        const availablePlanes = ids(g, AirportPlane).map(id => g.getTypedUnsafe(AirportPlane, id));
        let boardingOpen = availablePlanes.some(p => p.phase === 0 && p.boardingCalled && p.boarded < p.seats);
        
        const answeredQuestion = new Set<number>();
        let inquiryBusy = ids(g, AirportPassenger).some(id => g.getTypedUnsafe(AirportPassenger, id).state === 7);
        for (const id of ids(g, AirportPassenger)) {
            const p = g.getTypedUnsafe(AirportPassenger, id);
            if (p.arriving || p.stage !== 6) continue;
            if (!boardingOpen && !inquiryBusy && !p.inquired && p.serial % 5 === 0 && p.state === 6) {
                p.gate = availablePlanes.find(plane => plane.phase === 0)?.gate ?? 0;
                p.inquired = true; p.state = 7; p.remaining = 180;
                p.seat = -1; p.seated = false; inquiryBusy = true;
            }
            if (p.state !== 7) continue;
            answeredQuestion.add(p.gate);
            const questionSpot = inquiryPoint(p.gate, tier(state(g)).code);
            route(g, id, questionSpot);
            if (distance(g.getTypedUnsafe(Transform, id), questionSpot) < 8 && [...ids(g, AirportAgent), ...ids(g, AirportStaff).filter(a => { const f = g.getTypedUnsafe(AirportStaff, a); return worksDesk(f) && f.stage === 6; })].some(a => onPad('work', g.getTypedUnsafe(Transform, a), boardingWorkPoint(p.gate, tier(state(g)).code))) && --p.remaining <= 0) p.state = 6;
        }
        automaticBoarding(g, answeredQuestion);
        boardingOpen = availablePlanes.some(p => p.phase === 0 && p.boardingCalled && p.boarded < p.seats);
        const seats = waitingSeats(state(g).chairLevel, tier(state(g)).maxX);
        const usedSeats = new Set(ids(g, AirportPassenger).map(id => g.getTypedUnsafe(AirportPassenger, id).seat).filter(seat => seat >= 0));
        const boardingQueue = ids(g, AirportPassenger).filter(id => {
            const p = g.getTypedUnsafe(AirportPassenger, id);
            return !p.arriving && p.stage === 6 && p.state === 0;
        }).sort((a, b) => g.getTypedUnsafe(AirportPassenger, a).queuedAt - g.getTypedUnsafe(AirportPassenger, b).queuedAt);
        let queueCount = boardingQueue.length;
        for (const id of ids(g, AirportPassenger)) {
            const p = g.getTypedUnsafe(AirportPassenger, id);
            if (p.arriving || p.stage !== 6 || ![0, 6].includes(p.state)) continue;
            // A connector who has waited past their deadline misses the connection: a lost passenger and a bad review.
            if (isConnector(p.serial) && --p.remaining <= 0) {
                const e = state(g);
                if (shiftsActive(e)) e.shiftWalkouts++;
                notice(g, 'A connecting traveller missed their flight. Board connectors quickly at Metro.', 1);
                g.removeEntity(id);
                continue;
            }
            if (boardingOpen && p.state === 0 && availablePlanes.some(plane => plane.gate === p.gate && plane.phase === 0 && plane.boardingCalled && plane.boarded < plane.seats)) continue;
            // Most travelers line up at the call; a smaller group waits until the line clears.
            const gate = boardingGate(g);
            if (gate >= 0 && queueCount < 24 && (p.serial % 5 !== 0 || queueCount < 2)) { p.gate = gate; p.state = 0; p.queuedAt = g.frame; p.seat = -1; p.seated = false; resetPatience(p); queueCount++; continue; }
            p.state = 6; p.queueOrder = -1;
            if (p.seat < 0) {
                p.seat = seats.findIndex((_, seat) => !usedSeats.has(seat));
                if (p.seat >= 0) usedSeats.add(p.seat);
            }
            const seat = seats[p.seat];
            const target = seat ? { x: seat.approachX, y: seat.approachY } : { x: 200 + (p.serial % 5) * 210, y: 740 + Math.floor(p.serial % 15 / 5) * 30 };
            route(g, id, target);
            p.seated = !!seat && distance(g.getTypedUnsafe(Transform, id), target) < 6;
        }
        const all = ids(g, AirportPassenger); // refreshed: the pass above may have removed arrivals.
        // A captain can leave a manually operated optional department mid-service.
        // Release its existing passengers without a payout; core safety checks are never bypassed.
        for (const id of all) {
            const p = g.getTypedUnsafe(AirportPassenger, id);
            // International travellers wait at Passport Control even when nobody staffs it (MGT-009).
            if (p.arriving || p.entering || p.state > 2 || CORE_STAGES.includes(p.stage) || stageServing(p.stage, ss[p.stage]) || p.stage === PASSPORT && internationalTraveller(p.serial, ss)) continue;
            const next = nextStage(p.stage, ss, travellerRoute(g, p.serial, p.vip));
            if (next >= 0) { p.stage = next; p.state = 0; p.slot = -1; p.remaining = 0; resetPatience(p); }
        }
        const petCalm = new Set(shiftsActive(state(g)) ? ids(g, AirportPassenger).map(id => g.getTypedUnsafe(AirportPassenger, id)).filter(q => q.state <= 2 && !q.arriving && archetype(q.serial, q.vip, true) === 'pet').map(q => q.stage) : []);
        const act2 = shiftsActive(state(g)), fastTrack = act2 && state(g).fastTrack === 1 && ss[2].lanes >= 2;
        const premium = (q: { serial: number; vip: boolean }) => archetype(q.serial, q.vip, act2) === 'vip' || archetype(q.serial, q.vip, act2) === 'business';
        // MGT-001 trait: a Cheerful helper working a desk keeps its line patient 10% longer.
        const cheerful = new Set(ids(g, AirportStaff).map(id => g.getTypedUnsafe(AirportStaff, id)).filter(f => worksDesk(f) && staffTrait(f.recordId) === 'Cheerful').map(f => f.stage));
        ss.forEach((s, index) => {
            if (!s.unlocked)
                return;
            s.busy = 0; s.queueLength = 0;
            const groups = index === 6 ? availablePlanes.map(plane => plane.gate) : [0];
            for (const gate of groups) {
                const passengers = all.filter(id => { const p = g.getTypedUnsafe(AirportPassenger, id); return p.stage === index && (index !== 6 || p.gate === gate); }).sort((a, b) => {
                    const first = g.getTypedUnsafe(AirportPassenger, a), second = g.getTypedUnsafe(AirportPassenger, b);
                    // MGT-008: with Fast Track on, premium travellers (VIP, business) go to the front of the Security line.
                    if (index === 2 && fastTrack) { const pa = premium(first), pb = premium(second); if (pa !== pb) return pa ? -1 : 1; }
                    return index === 6 ? first.queuedAt - second.queuedAt : first.serial - second.serial;
                });
                const busy = passengers.filter(id => [1, 2].includes(g.getTypedUnsafe(AirportPassenger, id).state));
                const waiting = passengers.filter(id => g.getTypedUnsafe(AirportPassenger, id).state === 0);
                const joined = waiting.filter(id => g.getTypedUnsafe(AirportPassenger, id).queueOrder >= 0);
                const tail = joined.length;
                let nextOrder = Math.max(-1, ...joined.map(id => g.getTypedUnsafe(AirportPassenger, id).queueOrder)) + 1;
                for (const id of waiting) {
                    const p = g.getTypedUnsafe(AirportPassenger, id);
                    if (p.queueOrder >= 0) continue;
                    const at = g.getTypedUnsafe(Transform, id);
                    if (distance(at, queuePoint(index, tail, gate, tier(state(g)).code)) < 28) p.queueOrder = nextOrder++;
                }
                const queued = waiting.sort((a, b) => {
                    const first = g.getTypedUnsafe(AirportPassenger, a), second = g.getTypedUnsafe(AirportPassenger, b);
                    if (index === 2 && fastTrack && premium(first) !== premium(second)) return premium(first) ? -1 : 1;
                    return (first.queueOrder < 0 ? Infinity : first.queueOrder) - (second.queueOrder < 0 ? Infinity : second.queueOrder) || first.serial - second.serial;
                });
                const lineLength = queued.filter(id => g.getTypedUnsafe(AirportPassenger, id).queueOrder >= 0).length;
                s.busy += busy.length;
                s.queueLength += queued.length;
                // Bottleneck signifier, à la "No Seat!": an unmistakable queue cue, deterministic throttle.
                if (queued.length >= 12 && CORE_STAGES.includes(index) && g.frame % 1800 === 0)
                    notice(g, s.activeWorkers
                        ? `${CONFIG.stages[index].name} queue is long — ${index === 6 ? 'improve boarding speed' : 'add speed or dividers'}.`
                        : `${CONFIG.stages[index].name} needs an operator — stand at the work circle or let a helper cover it.`, 1);
                const used = new Set(busy.map(id => g.getTypedUnsafe(AirportPassenger, id).slot));
                queued.forEach((id, rank) => {
                    const p = g.getTypedUnsafe(AirportPassenger, id);
                
                    const place = p.queueOrder >= 0 ? rank : lineLength;
                    const point = queuePoint(index, place, gate, tier(state(g)).code);
                    queueRoute(g, id, index, place, Math.max(0, lineLength - 1), gate);
                    const at = g.getTypedUnsafe(Transform, id);
                    // A snack buyer tosses the wrapper at the nearest bin; a miss litters wherever it lands. While the
                    // cleanup lesson waits on a clean floor, the next traveller in line drops one so it never stalls.
                    const teach = index === 2 && currentMission(state(g))?.metric === 'trashDisposed' && !ids(g, AirportTrash).length;
                    if ((p.snack || teach) && index === 2 && distance(at, point) < 26) {
                        p.snack = false;
                        const bin = BINS.map(binPad).filter(b => b.x < tier(state(g)).maxX).sort((a, b) => distance(at, a) - distance(at, b))[0];
                        if (teach || p.serial % 3 !== 0 || !bin || g.rand.int(0, 1) === 1)
                            litter(g, at, p.serial);
                    }
                    if (index !== 6) {
                        p.sad = nearTrash(at);
                        const e = state(g);
                        p.leaving = 0;
                        const drain = (p.sad ? 1.5 : 1) * (e.relaxed ? 0.5 : 1) * (s.manager ? 0.5 : 1) * (cheerful.has(index) ? 0.9 : 1) / (archetype(p.serial, p.vip, shiftsActive(e)) === 'business' ? BUSINESS_PATIENCE : 1) * (shiftsActive(e) ? crisisMood(e, index) : 1) * (petCalm.has(index) ? PET_CALM : 1);
                        p.patience = Math.max(0, p.patience - drain);
                        // Mood survives station changes; service restores it gradually instead of erasing a bad wait.
                        if (p.patience === 0) p.happiness = Math.max(10, p.happiness - 0.05 * drain);
                        else if (p.sad) p.happiness = Math.max(10, p.happiness - 0.01);
                    }
                    // The Fast Track lane is reserved: general travellers get one lane fewer while premium may use any lane.
                    const lanes = index === 2 && fastTrack && !premium(p) ? capacity(s) - 1 : capacity(s);
                    if (p.queueOrder < 0 || rank !== 0 || distance(g.getTypedUnsafe(Transform, id), point) > 8 || busy.length >= lanes)
                        return;
                    let slot = 0;
                    while (used.has(slot))
                        slot++;
                    used.add(slot);
                    p.state = 1;
                    p.slot = slot;
                    // Business travellers take twice the handling at a desk (MGT-012).
                    p.remaining = serviceFrames(index, s, p.serial) * (index !== 6 && archetype(p.serial, p.vip, shiftsActive(state(g))) === 'business' ? BUSINESS_HANDLING : 1)
                        * (index === 4 && shiftsActive(state(g)) ? RETAIL_MODULES[state(g).retailModule].service : 1);
                    route(g, id, servicePoint(index, slot, capacity(s), gate, tier(state(g)).code), true);
                });
                busy.forEach(id => { const p = g.getTypedUnsafe(AirportPassenger, id);
                    // Existing saves may still carry the old fixed boarding timer.
                    if (index === 6) p.remaining = Math.min(p.remaining, serviceFrames(index, s, p.serial));
                    if (p.state === 1 && distance(g.getTypedUnsafe(Transform, id), servicePoint(index, p.slot, capacity(s), gate, tier(state(g)).code)) < 10)
                    p.state = 2; });
            }
        });
        // Vending is a short physical detour after check-in, not another mandatory department.
        const machines = vending(g);
        const claimed = new Set<number>();
        for (const id of all) {
            const p = g.getTypedUnsafe(AirportPassenger, id);
            if (p.state !== 4) continue;
            const v = machines[p.vending], target = vendingPoint(p.vending);
            route(g, id, target);
            if (distance(g.getTypedUnsafe(Transform, id), target) > 14 || claimed.has(p.vending)) continue;
            claimed.add(p.vending); // One customer per physical machine per tick, oldest entity first.
            p.shopFrames -= 1 + v.speedLevel * 0.25;
            if (p.shopFrames > 0) continue;
            const e = state(g);
            const sold = deposit(e, v, vendingPayout(v, e, g.frame, career(g).prestige));
            if (shiftsActive(e)) { e.shiftEarned += sold; e.shiftRetail += sold; }
            v.sold++; e.vendingSales++;

            p.snack = true; // the wrapper lands near Security, not at the machine
            p.vending = -1; p.state = 0; resetPatience(p);
        }
        const departures = all.map(id => g.getTypedUnsafe(AirportPassenger, id)).filter(p => !p.arriving);
        state(g).passengerHappiness = departures.length ? Math.round(departures.reduce((sum, p) => sum + p.happiness, 0) / departures.length) : 80;
        const planes = ids(g, AirportPlane).map(id => g.getTypedUnsafe(AirportPlane, id));
        for (const id of all) {
            const p = g.getTypedUnsafe(AirportPassenger, id);
            if (p.state !== 3) continue;
            const plane = planes.find(plane => plane.gate === p.gate);
            if (!plane || plane.phase !== 0) continue;
            const walk = gateWalk(tier(state(g)).code, p.gate), target = walk[p.walkStep];
            if (p.walkStep < 2) route(g, id, target); else walkStraight(g, id, target);
            if (distance(g.getTypedUnsafe(Transform, id), target) >= 6) continue;
            if (++p.walkStep < walk.length) continue;
            if (plane.boarded < plane.seats) { plane.boarded++; g.removeEntity(id); }
        }

    };
}
